<template>
    <div>
        <div
            class="slider-gallery"
            :class="mode && `slider-gallery--mode-${mode}`"
        >
            <div class="slider-gallery__main-wrapper">
                <swiper
                    class="slider-gallery__main"
                    :slides-per-view="1"
                    :thumbs="thumbs"
                    :modules="mainModules"
                    :pagination="{
                        clickable: true,
                        bulletClass: 'slider-gallery__pagination-bullet',
                        bulletActiveClass:
                            'slider-gallery__pagination-bullet--active',
                    }"
                    :autoHeight="autoHeight"
                    :initialSlide="activeImageIndex"
                    effect="fade"
                    @swiper="setMainSwiper"
                    @slideChange="activeImageIndex = $event.realIndex"
                >
                    <swiper-slide v-for="(image, i) of images">
                        <img
                            class="slider-gallery__main-slide"
                            :class="{
                                'slider-gallery__main-slide--fit-contain':
                                    imageFitContain,
                                'slider-gallery__main-slide--clickable':
                                    isClickable,
                            }"
                            :src="image || ''"
                            alt=""
                            @click="isClickable && onMainImageClick()"
                        />
                    </swiper-slide>
                </swiper>
                <button
                    v-if="mode === 'full' && images.length > 1"
                    class="slider-gallery__full-nav-btn slider-gallery__full-nav-btn--prev"
                    :disabled="mainSwiper?.isBeginning"
                    @click="mainSwiper?.slidePrev()"
                >
                    <NavChevronLeftIcon
                        class="slider-gallery__full-nav-btn-icon"
                    />
                </button>
                <button
                    v-if="mode === 'full' && images.length > 1"
                    class="slider-gallery__full-nav-btn slider-gallery__full-nav-btn--next"
                    :disabled="mainSwiper?.isEnd"
                    @click="mainSwiper?.slideNext()"
                >
                    <NavChevronLeftIcon
                        class="slider-gallery__full-nav-btn-icon"
                    />
                </button>
            </div>

            <div
                v-if="hasThumbnails"
                class="slider-gallery__thumbs-wrapper"
            >
                <swiper
                    class="slider-gallery__thumbs"
                    :spaceBetween="20"
                    :slidesPerView="3"
                    :watchSlidesProgress="true"
                    :modules="modules"
                    :initialSlide="activeImageIndex"
                    @swiper="setThumbsSwiper"
                >
                    <swiper-slide v-for="(image, i) of images">
                        <img
                            class="slider-gallery__thumbs-slide"
                            :src="image || ''"
                            alt=""
                        />
                    </swiper-slide>
                </swiper>

                <NavChevronLeftIcon
                    v-if="images.length > 3"
                    class="slider-gallery__nav-btn slider-gallery__nav-btn--prev"
                    :class="
                        thumbsSwiper?.isBeginning &&
                        'slider-gallery__nav-btn--disabled'
                    "
                    @click="thumbsSwiper?.slidePrev()"
                />

                <NavChevronLeftIcon
                    v-if="images.length > 3"
                    class="slider-gallery__nav-btn slider-gallery__nav-btn--next"
                    :class="
                        thumbsSwiper?.isEnd &&
                        'slider-gallery__nav-btn--disabled'
                    "
                    @click="thumbsSwiper?.slideNext()"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    export interface SliderGalleryProps {
        images: string[];
        imageFitContain?: boolean;
        mode?: 'house' | 'plot' | 'full';
        defaultActiveIndex?: number;
    }
</script>

<script setup lang="ts">
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { Pagination, Thumbs, EffectFade } from 'swiper/modules';
    import { Swiper as SwiperType } from 'swiper/types';
    import { computed, ref } from 'vue';
    import NavChevronLeftIcon from '../commons/icons/NavChevronLeftIcon.vue';
    import { cardSettingsStore } from '@/store/modules/CardSettings';
    import { galleryStore } from '@/store/modules/Gallery';
    import { DESKTOP_TOP, XL_BOTTOM } from '@/constants/sizes';
    import 'swiper/css';
    import 'swiper/css/pagination';
    import 'swiper/css/thumbs';
    import 'swiper/css/effect-fade';

    const props = defineProps<SliderGalleryProps>();

    const activeImageIndex = ref(props.defaultActiveIndex || 0);

    const mainSwiper = ref<SwiperType | null>(null);
    const thumbsSwiper = ref<SwiperType | null>(null);

    const modules = [Thumbs, Pagination];
    const mainModules = [...modules, EffectFade];

    const hasThumbnails = computed(
        () =>
            (props.mode === 'plot' || props.mode === 'house') &&
            props.images.length > 1,
    );

    const autoHeight = computed(() => props.mode === 'full');

    const thumbs = computed(() =>
        hasThumbnails
            ? {
                  swiper: thumbsSwiper.value,
              }
            : {},
    );

    const isClickable = computed(() => props.mode !== 'full');

    const setThumbsSwiper = (swiper: SwiperType) => {
        thumbsSwiper.value = swiper;
    };

    const setMainSwiper = (swiper: SwiperType) => {
        mainSwiper.value = swiper;
    };

    const onMainImageClick = () => {
        galleryStore.SET_IMAGES(props.images);
        galleryStore.SET_ACTIVE_INDEX(activeImageIndex.value);
        galleryStore.SET_SHOW_MODAL(true);
    };
</script>

<style lang="less">
    .slider-gallery {
        &--mode-plot {
            .slider-gallery__main {
                height: 294px;

                @media (max-width: @size-desktop-top) {
                    height: 409px;
                }

                @media (max-width: @size-mobile-top) {
                    height: 205px;
                }
            }
        }

        &--mode-house {
            .slider-gallery__main {
                height: 294px;

                @media (max-width: @size-desktop-top) {
                    height: 415px;
                }

                @media (max-width: @size-mobile-top) {
                    height: 205px;
                }
            }
        }

        &--mode-full {
            .slider-gallery__main-slide {
                border-radius: 40px;

                @media (max-width: @size-desktop-top) {
                    border-radius: 0;
                }
            }

            .swiper-pagination {
                display: none;
            }
        }

        &__main-wrapper {
            position: relative;
        }

        &__main {
            &-slide {
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;

                &--fit-contain {
                    object-fit: contain;
                }

                &--clickable {
                    cursor: pointer;
                }
            }
        }

        &__thumbs-wrapper {
            position: relative;
            padding-left: 41px;
            padding-right: 41px;
            margin-top: 22px;

            @media (max-width: @size-desktop-top) {
                display: none;
            }
        }

        // .slider-gallery__thumbs
        &__thumbs {
            height: 69px;

            // .slider-gallery__thumb-slide
            &-slide {
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;
                cursor: pointer;
            }
        }

        &__nav-btn {
            position: absolute;
            top: 50%;
            width: 24px;
            height: 24px;
            color: @warning;
            cursor: pointer;
            user-select: none;

            &--prev {
                left: 0;
                transform: translateY(-50%);
            }

            &--next {
                right: 0;
                transform: translateY(-50%) rotate(180deg);
            }

            &--disabled {
                opacity: 0.3;
                cursor: default;
                pointer-events: none;
            }
        }

        &__full-nav-btn {
            position: absolute;
            top: 50%;
            z-index: 1;
            width: 55px;
            height: 55px;
            background-color: @simple-light;
            cursor: pointer;
            user-select: none;
            transition: all 0.3s ease;
            border: 0;

            @media (hover: hover) {
                &:hover {
                    background-color: @success;

                    .slider-gallery__full-nav-btn-icon {
                        color: @simple-light;
                    }
                }
            }

            @media (max-width: @size-mobile-top) {
                display: none;
            }

            &--prev {
                left: 16px;
                transform: translateY(-50%);
            }

            &--next {
                right: 16px;
                transform: translateY(-50%) rotate(180deg);
            }

            &[disabled] {
                opacity: 0.3;
                cursor: default;
                pointer-events: none;
            }
        }

        &__full-nav-btn-icon {
            color: @success;
            transition: all 0.3s ease;
        }

        &__pagination-bullet {
            display: inline-block;
            width: 6px;
            height: 6px;
            margin: 0 2px;
            background-color: @simple-light;
            border-radius: 100%;

            &--active {
                background-color: @warning;
            }
        }

        .swiper-pagination {
            @media (min-width: @size-xl-bottom) {
                display: none;
            }
        }
    }
</style>
